import { useQuery } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Dumbbell, Edit3, Plus, Search, X } from 'lucide-react'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useAdminApi } from '@app/api/api-client-context'
import { useAuth } from '@app/auth/auth-context'
import { exerciseKeys, lifecycleLabel, type Exercise, type ExerciseTaxonomies } from '@entities/exercise'
import { getNextCursor, unwrapApiResponse } from '@shared/api'
import { interfaceMessages } from '@shared/i18n/interface-messages'
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@shared/ui/page'
import {
	defaultExerciseCatalogFilters,
	parseExerciseCatalogFilters,
	serializeExerciseCatalogFilters,
	type ExerciseCatalogFilters,
} from '../model/catalog-filters'
import { ExerciseEditor } from './editor'

const lifecycleClass: Record<Exercise['lifecycle'], string> = {
	DRAFT: 'bg-slate-100 text-slate-700',
	PUBLISHED: 'bg-emerald-100 text-emerald-800',
	ARCHIVED: 'bg-amber-100 text-amber-800',
}

const formatDate = (value?: string) => value
	? new Intl.DateTimeFormat('ru-RU', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
	: '—'

export const ExerciseCatalog = ({ taxonomies }: { taxonomies: ExerciseTaxonomies }) => {
	const api = useAdminApi()
	const { can } = useAuth()
	const location = useLocation()
	const history = useHistory()
	const filters = useMemo(() => parseExerciseCatalogFilters(location.search), [location.search])
	const selectedExerciseId = useMemo(() => new URLSearchParams(location.search).get('exercise'), [location.search])
	const [searchDraft, setSearchDraft] = useState(filters.search)
	const [cursor, setCursor] = useState<string | null>(null)
	const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([])
	const [editor, setEditor] = useState<{ open: boolean; id: string | null }>({ open: Boolean(selectedExerciseId), id: selectedExerciseId })

	useEffect(() => {
		setSearchDraft(filters.search)
		setCursor(null)
		setCursorHistory([])
	}, [filters.search, filters.lifecycle, filters.difficulty, filters.equipment, filters.exerciseType, filters.sort, filters.direction, filters.limit])

	useEffect(() => {
		setEditor(selectedExerciseId ? { open: true, id: selectedExerciseId } : { open: false, id: null })
	}, [selectedExerciseId])

	const queryFilters = useMemo(() => ({ ...filters, cursor }), [filters, cursor])
	const query = useQuery({
		queryKey: exerciseKeys.list(queryFilters),
		queryFn: async () => unwrapApiResponse(await api.GET('/admin/v1/exercises', {
			params: {
				query: {
					cursor: cursor ?? undefined,
					limit: filters.limit,
					search: filters.search || undefined,
					lifecycle: filters.lifecycle ? filters.lifecycle as Exercise['lifecycle'] : undefined,
					difficulty: filters.difficulty ? [filters.difficulty] : undefined,
					equipment: filters.equipment ? [filters.equipment] : undefined,
					exercise_type: filters.exerciseType ? [filters.exerciseType] : undefined,
					sort: filters.sort,
					direction: filters.direction,
				},
			},
		})).data,
		placeholderData: previous => previous,
	})

	const updateFilters = (patch: Partial<ExerciseCatalogFilters>) => {
		const next = { ...filters, ...patch }
		history.replace({ pathname: location.pathname, search: serializeExerciseCatalogFilters(next) })
	}
	const submitSearch = (event: FormEvent) => {
		event.preventDefault()
		updateFilters({ search: searchDraft })
	}
	const resetFilters = () => {
		setSearchDraft('')
		history.replace({ pathname: location.pathname, search: '' })
	}
	const goNext = () => {
		const next = getNextCursor(query.data?.page)
		if (!next) return
		setCursorHistory(current => [...current, cursor])
		setCursor(next)
	}
	const goPrevious = () => {
		setCursorHistory(current => {
			if (!current.length) return current
			setCursor(current[current.length - 1])
			return current.slice(0, -1)
		})
	}
	const toggleSort = (sort: ExerciseCatalogFilters['sort']) => {
		if (filters.sort === sort) updateFilters({ direction: filters.direction === 'asc' ? 'desc' : 'asc' })
		else updateFilters({ sort, direction: sort === 'name' ? 'asc' : 'desc' })
	}
	const SortIcon = filters.direction === 'asc' ? ArrowUp : ArrowDown
	const hasFilters = Boolean(filters.search || filters.lifecycle || filters.difficulty || filters.equipment || filters.exerciseType)

	return (
		<section className='space-y-6'>
			<PageHeader
				eyebrow={interfaceMessages.sections.exercises}
				title='Упражнения'
				description='Каталог, свойства, мышечные группы, публикация и безопасный контроль версий.'
				actions={can('createExercise') ? (
					<Button onClick={() => setEditor({ open: true, id: null })}><Plus className='h-4 w-4' /> Создать упражнение</Button>
				) : undefined}
			/>

			<Card>
				<CardContent className='space-y-4 p-4 sm:p-5'>
					<form onSubmit={submitSearch} className='flex flex-col gap-2 sm:flex-row'>
						<div className='relative flex-1'>
							<Search className='pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
							<Input aria-label='Поиск упражнений' value={searchDraft} onChange={event => setSearchDraft(event.target.value)} placeholder='Поиск по названию, описанию и технике' className='pl-9' />
						</div>
						<Button type='submit' variant='outline'>Найти</Button>
						{hasFilters ? <Button type='button' variant='ghost' onClick={resetFilters}><X className='h-4 w-4' /> Сбросить</Button> : null}
					</form>
					<div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-5'>
						<select value={filters.lifecycle} onChange={event => updateFilters({ lifecycle: event.target.value })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Статус'>
							<option value=''>Все статусы</option>
							<option value='DRAFT'>Черновики</option>
							<option value='PUBLISHED'>Опубликованные</option>
							<option value='ARCHIVED'>Архивные</option>
						</select>
						<select value={filters.exerciseType} onChange={event => updateFilters({ exerciseType: event.target.value })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Тип упражнения'>
							<option value=''>Все типы</option>
							{taxonomies.types.filter(item => item.active).map(item => <option key={item.code} value={item.code}>{item.name}</option>)}
						</select>
						<select value={filters.difficulty} onChange={event => updateFilters({ difficulty: event.target.value })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Сложность'>
							<option value=''>Любая сложность</option>
							{taxonomies.difficulties.filter(item => item.active).map(item => <option key={item.code} value={item.code}>{item.name}</option>)}
						</select>
						<select value={filters.equipment} onChange={event => updateFilters({ equipment: event.target.value })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Оборудование'>
							<option value=''>Любое оборудование</option>
							{taxonomies.equipment.filter(item => item.active).map(item => <option key={item.code} value={item.code}>{item.name}</option>)}
						</select>
						<select value={filters.limit} onChange={event => updateFilters({ limit: Number(event.target.value) })} className='h-10 rounded-md border bg-background px-3 text-sm' aria-label='Размер страницы'>
							{[10, 25, 50, 100].map(limit => <option key={limit} value={limit}>{limit} на странице</option>)}
						</select>
					</div>
				</CardContent>
			</Card>

			{query.isPending ? <LoadingState label='Загрузка каталога упражнений…' /> : null}
			{query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
			{query.data && query.data.items.length === 0 ? (
				<EmptyState title='Упражнения не найдены' description={hasFilters ? 'Измените параметры поиска или сбросьте фильтры.' : 'Создайте первое упражнение для административного каталога.'} action={hasFilters ? <Button variant='outline' onClick={resetFilters}>Сбросить фильтры</Button> : undefined} />
			) : null}
			{query.data && query.data.items.length > 0 ? (
				<Card className='overflow-hidden'>
					<div className='overflow-x-auto'>
						<table className='w-full min-w-[940px] text-left text-sm'>
							<caption className='sr-only'>Каталог упражнений</caption>
							<thead className='border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground'>
								<tr>
									<th scope='col' className='px-4 py-3'>Упражнение</th>
									<th scope='col' className='px-4 py-3' aria-sort={filters.sort === 'lifecycle' ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><button type='button' onClick={() => toggleSort('lifecycle')} className='inline-flex items-center gap-1'>Статус {filters.sort === 'lifecycle' ? <SortIcon className='h-3 w-3' aria-hidden='true' /> : null}</button></th>
									<th scope='col' className='px-4 py-3'>Тип</th>
									<th scope='col' className='px-4 py-3'>Сложность</th>
									<th scope='col' className='px-4 py-3'>Мышцы</th>
									<th scope='col' className='px-4 py-3' aria-sort={filters.sort === 'updated_at' ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'}><button type='button' onClick={() => toggleSort('updated_at')} className='inline-flex items-center gap-1'>Изменено {filters.sort === 'updated_at' ? <SortIcon className='h-3 w-3' aria-hidden='true' /> : null}</button></th>
									<th scope='col' className='px-4 py-3 text-right'>Действия</th>
								</tr>
							</thead>
							<tbody className='divide-y'>
								{query.data.items.map(exercise => (
									<tr key={exercise.id} className='hover:bg-muted/30'>
										<td className='px-4 py-3'>
											<div className='flex items-center gap-3'>
												{exercise.media?.[0]?.url ? <img src={exercise.media[0].url} alt='' className='h-11 w-11 rounded-lg object-cover' /> : <div className='flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary'><Dumbbell className='h-5 w-5' /></div>}
												<div className='min-w-0'><p className='max-w-xs truncate font-medium'>{exercise.name}</p><p className='text-xs text-muted-foreground'>ID {exercise.id} · v{exercise.version}</p></div>
											</div>
										</td>
										<td className='px-4 py-3'><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${lifecycleClass[exercise.lifecycle]}`}>{lifecycleLabel[exercise.lifecycle]}</span></td>
										<td className='px-4 py-3'>{taxonomies.types.find(item => item.code === exercise.exercise_type)?.name ?? exercise.exercise_type}</td>
										<td className='px-4 py-3'>{taxonomies.difficulties.find(item => item.code === exercise.difficulty_code)?.name ?? exercise.difficulty_code ?? '—'}</td>
										<td className='px-4 py-3'>{exercise.muscles?.length ?? 0}</td>
										<td className='px-4 py-3 text-muted-foreground'>{formatDate(exercise.updated_at)}</td>
										<td className='px-4 py-3 text-right'><Button variant='ghost' size='sm' onClick={() => { const params = new URLSearchParams(location.search); params.set('exercise', exercise.id); history.replace({ pathname: location.pathname, search: `?${params.toString()}` }) }}><Edit3 className='h-4 w-4' /> Открыть</Button></td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div className='flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
						<p className='text-sm text-muted-foreground' role='status' aria-live='polite'>Показано {query.data.items.length} из {query.data.page.total}</p>
						<div className='flex gap-2'>
							<Button variant='outline' size='sm' disabled={cursorHistory.length === 0 || query.isFetching} onClick={goPrevious}><ChevronLeft className='h-4 w-4' /> Назад</Button>
							<Button variant='outline' size='sm' disabled={!getNextCursor(query.data.page) || query.isFetching} onClick={goNext}>Далее <ChevronRight className='h-4 w-4' /></Button>
						</div>
					</div>
				</Card>
			) : null}

			<ExerciseEditor open={editor.open} exerciseId={editor.id} taxonomies={taxonomies} onClose={() => { const params = new URLSearchParams(location.search); params.delete('exercise'); history.replace({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }) }} />
		</section>
	)
}
